/**
 * Studio v2 — shared icon glyphs (#5320).
 *
 * The Studio "palette mark" (four dots + a scooped-thumb outline) and a
 * generic circular "!" error glyph were each hand-duplicated as literal SVG
 * markup across call sites — the sidebar rail icon, the About-dialog icon,
 * the Pages module's Themes-tab button + grid theme badge, and the brand-kit
 * library's empty/error states. One copy here, parameterized by the handful
 * of dimensions that actually differ per site (size/stroke width/color) so
 * every site keeps its EXACT current rendering.
 *
 * Lives in `modules/shared/` rather than `modules/pages/`: it is consumed by
 * both `layout/` (SidebarRail, a composition layer — same pattern as this
 * directory's `colors.ts` via `SidebarModuleItemRow`) AND a PEER module
 * (`modules/_infrastructure`). `modules/pages` already depends on
 * `modules/_infrastructure` (ModuleEmptyState) — a reverse
 * `_infrastructure → pages` edge would run against that established
 * direction, so this cross-module glyph belongs in the sanctioned
 * cross-module location instead (see this directory's `integrationAccounts.ts`
 * for the same layout+module-import precedent).
 */
import type { CSSProperties, ReactNode } from 'react';

export interface StudioIconProps {
  size?: number;
  strokeWidth?: number;
  /** Defaults to `currentColor` — most call sites color via CSS. */
  color?: string;
  className?: string;
  style?: CSSProperties;
  'aria-hidden'?: boolean;
}

/** The Studio palette mark — four dots + a scooped-thumb-hole outline. */
export function StudioMarkIcon({
  size = 20,
  strokeWidth = 2,
  color = 'currentColor',
  className,
  style,
  ...rest
}: StudioIconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      {...rest}
    >
      <circle cx="13.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="10.5" r="2.5" />
      <circle cx="8.5" cy="7.5" r="2.5" />
      <circle cx="6.5" cy="12.5" r="2.5" />
      <path d="M12 2a10 10 0 0 0 0 20 1.5 1.5 0 0 0 1.06-2.56 1.5 1.5 0 0 1 1.06-2.56h1.78A4.04 4.04 0 0 0 21 12.78 10 10 0 0 0 12 2z" />
    </svg>
  );
}

/** Generic circular "!" error glyph (fetch-error empty states). */
export function StudioErrorIcon({
  size = 28,
  strokeWidth = 1.5,
  color = '#dc2626',
  className,
  style,
  ...rest
}: StudioIconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      {...rest}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
